/**
 * Blue Speed Construction — SEO source of truth.
 *
 * Consumed in two places, so the live site and the files search engines read can
 * never drift apart:
 *   1. `src/config/seo.js` — applies title / description / canonical / Open Graph
 *      tags on every route change.
 *   2. `scripts/generate-seo-files.js` — writes public/sitemap.xml and
 *      public/robots.txt, and runs automatically before `npm run build`.
 *
 * ⚠️ BEFORE LAUNCH: set SITE_URL to the real domain. Everything else follows
 * from it — canonical tags, og:url, the sitemap and the robots.txt Sitemap line.
 *
 * Written as CommonJS so the Node build script and webpack can both read it.
 */

/** Production origin. No trailing slash. */
const SITE_URL = "https://bluespeed.web.app";

/** Falls back onto any page that does not set its own. */
const DEFAULT_IMAGE = "/bluespeed-social-preview.jpg";

const SITE_NAME = "Blue Speed Construction";

/**
 * One entry per indexable route.
 *
 * `title` is what shows as the blue link in Google, so keep it under about 60
 * characters or it gets truncated. `description` is the grey text underneath —
 * aim for 140–160 characters. Both must be unique per page; duplicates are the
 * single most common reason pages get filtered out of results.
 */
const PAGES = [
  {
    path: "/",
    title: "Blue Speed Construction | Rope Access & Building Maintenance",
    description:
      "Singapore building maintenance and construction since 1996. Civil works, façade cleaning, painting and specialist rope access. BCA registered CW01, CR09, FM02.",
    priority: "1.0",
    changefreq: "monthly",
  },
  {
    path: "/about",
    title: "About Blue Speed Construction | Singapore Since 1996",
    description:
      "Established in 1996, Blue Speed Construction grew from subcontract works to main contractor scopes in Singapore. BCA registered under CW01, CR09 and FM02.",
    priority: "0.7",
    changefreq: "yearly",
  },
  {
    path: "/services",
    title: "Services | Building Maintenance & Rope Access | Blue Speed",
    description:
      "General building maintenance, renovation and reinstatement, façade cleaning, painting, sealant works, cladding replacement and Periodic Façade Inspection (PFI).",
    priority: "0.9",
    changefreq: "monthly",
  },
  {
    path: "/divisions",
    title: "Our Divisions | Building & Civil, Rope Access | Blue Speed",
    description:
      "Two divisions: Building and Civil for construction, repair and reinstatement works; Rope Access for façade works at height without scaffolding or gondolas.",
    priority: "0.8",
    changefreq: "yearly",
  },
  {
    path: "/our-work",
    title: "Our Work | Project Photographs | Blue Speed Construction",
    description:
      "Photographs from live Blue Speed sites in Singapore — façade cleaning, painting, sealant and cladding works on rope, plus repair, waterproofing and reinstatement.",
    priority: "0.8",
    changefreq: "monthly",
  },
  {
    path: "/quality-safety",
    title: "Quality, Safety & Training | Blue Speed Construction",
    description:
      "How Blue Speed manages quality, safety and training: site-specific risk assessments, safe work procedures, permit-to-work requirements and workforce training.",
    priority: "0.7",
    changefreq: "yearly",
  },
  {
    path: "/testimonial",
    title: "Client Feedback | Blue Speed Construction",
    description:
      "What clients say about working with Blue Speed Construction on building maintenance, reinstatement and rope access projects across Singapore.",
    priority: "0.6",
    changefreq: "monthly",
  },
  {
    path: "/contact",
    title: "Contact Us | Blue Speed Construction Singapore",
    description:
      "Contact Blue Speed Construction in Singapore. Send your project scope, building type and site location, or message us on WhatsApp for a quick reply.",
    priority: "0.7",
    changefreq: "yearly",
  },
];

module.exports = { SITE_URL, SITE_NAME, DEFAULT_IMAGE, PAGES };
